import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { useSearchParams } from 'react-router-dom';
import { patientAPI } from '../../utils/api';

const Section = ({ title, icon, count, color, children }) => {
    const [open, setOpen] = useState(true);
    return (
        <div style={{ background: 'white', borderRadius: 10, marginBottom: 16, boxShadow: '0 1px 4px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
            <div onClick={() => setOpen(o => !o)} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', cursor: 'pointer', borderLeft: `4px solid ${color}`, background: '#f8fafc' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 16 }}>{icon}</span>
                    <span style={{ fontWeight: 700, fontSize: 14, color: '#1e3a5f' }}>{title}</span>
                    <span style={{ background: color, color: 'white', borderRadius: 10, padding: '1px 8px', fontSize: 11, fontWeight: 600 }}>{count}</span>
                </div>
                <span style={{ color: '#9ca3af', fontSize: 12 }}>{open ? '▲' : '▼'}</span>
            </div>
            {open && <div style={{ padding: 16 }}>{children}</div>}
        </div>
    );
};

const Row = ({ label, value }) => value ? (
    <div style={{ display: 'flex', marginBottom: 4 }}>
        <span style={{ minWidth: 140, fontSize: 12, color: '#6b7280', fontWeight: 600 }}>{label}:</span>
        <span style={{ fontSize: 12, color: '#111827' }}>{value}</span>
    </div>
) : null;

export default function PatientHistory() {
    const [searchParams] = useSearchParams();
    const [mrn, setMrn] = useState(searchParams.get('mrn') || '');
    const [patient, setPatient] = useState(null);
    const [history, setHistory] = useState(null);
    const [loading, setLoading] = useState(false);

    const search = async (autoMrn) => {
        const q = (autoMrn && typeof autoMrn === 'string' ? autoMrn : mrn).trim();
        if (!q) return toast.error('Enter MR#');
        if (q !== mrn) setMrn(q);
        setLoading(true);
        setPatient(null);
        setHistory(null);
        try {
            const res = await patientAPI.search(q);
            const p = res.data.patients?.[0];
            if (!p) { toast.error('Patient not found'); setLoading(false); return; }
            setPatient(p);
            const h = await patientAPI.getHistory(p.id);
            setHistory(h.data);
        } catch { toast.error('Failed to load history'); }
        setLoading(false);
    };

    useEffect(() => {
        const p = searchParams.get('mrn');
        if (p) search(p);
    }, []); // eslint-disable-line

    const handleKey = e => { if (e.key === 'Enter') search(); };

    const printHistory = () => {
        if (!patient || !history) return;

        const opdRows = (history.opd || []).map(v => `
            <div class="visit-block">
                <div class="visit-header">OPD Visit — ${v.visit_date || new Date(v.created_at).toLocaleDateString('en-PK')} | ${v.department || ''} | Dr. ${v.doctor || 'N/A'}</div>
                ${v.chief_complaint ? `<p><b>Chief Complaint:</b> ${v.chief_complaint}</p>` : ''}
                ${v.vitals ? `<p><b>Vitals:</b> ${typeof v.vitals === 'object' ? JSON.stringify(v.vitals) : v.vitals}</p>` : ''}
                ${v.diagnosis ? `<p><b>Diagnosis:</b> ${v.diagnosis}</p>` : ''}
                ${v.doctor_notes ? `<p><b>Doctor Notes:</b> ${v.doctor_notes}</p>` : ''}
                ${v.prescription ? `<p><b>Prescription:</b> ${typeof v.prescription === 'object' ? JSON.stringify(v.prescription) : v.prescription}</p>` : ''}
                ${v.advice ? `<p><b>Advice:</b> ${v.advice}</p>` : ''}
                ${v.follow_up_date ? `<p><b>Follow-up:</b> ${v.follow_up_date}</p>` : ''}
            </div>`).join('');

        const ipdRows = (history.ipd || []).map(a => `
            <div class="visit-block">
                <div class="visit-header">IPD — ${a.admission_number || ''} | ${a.ward_name || ''} | Bed: ${a.bed_number || ''} | ${a.admission_date || ''} → ${a.discharge_date || 'Still Admitted'}</div>
                ${a.diagnosis ? `<p><b>Diagnosis:</b> ${a.diagnosis}</p>` : ''}
                ${a.consultant_name ? `<p><b>Consultant:</b> ${a.consultant_name}</p>` : ''}
                ${a.discharge_type ? `<p><b>Discharge Type:</b> ${a.discharge_type}</p>` : ''}
                ${a.discharge_summary ? `<p><b>Discharge Summary:</b> ${a.discharge_summary}</p>` : ''}
                ${a.cause_of_death ? `<p><b>Cause of Death:</b> ${a.cause_of_death}</p>` : ''}
                ${(a.medications || []).length > 0 ? `<p><b>Medications:</b></p><ul>${a.medications.map(m => `<li>${m.name} ${m.dose || ''} ${m.route || ''} ${m.frequency || ''} ${m.duration || ''}</li>`).join('')}</ul>` : ''}
            </div>`).join('');

        const labRows = (history.lab || []).map(o => `
            <div class="visit-block">
                <div class="visit-header">Lab — ${o.order_number || ''} | ${new Date(o.created_at).toLocaleDateString('en-PK')} | ${o.status || ''}</div>
                ${(o.tests || []).filter(t => t.test).map(t => `<p>${t.test}: <b>${t.result || 'Pending'}</b> ${t.unit || ''} ${t.flag ? `(${t.flag})` : ''}</p>`).join('')}
            </div>`).join('');

        const radRows = (history.radiology || []).map(r => `
            <div class="visit-block">
                <div class="visit-header">Radiology — ${r.order_number || ''} | ${new Date(r.created_at).toLocaleDateString('en-PK')} | ${r.modality || ''} | ${r.body_part || ''}</div>
                ${r.clinical_indication ? `<p><b>Indication:</b> ${r.clinical_indication}</p>` : ''}
                ${r.findings ? `<p><b>Findings:</b> ${r.findings}</p>` : ''}
                ${r.impression ? `<p><b>Impression:</b> ${r.impression}</p>` : ''}
            </div>`).join('');

        const html = `<!DOCTYPE html><html><head><title>Patient History — ${patient.mrn}</title>
        <style>
            body { font-family: Arial, sans-serif; font-size: 12px; margin: 20px; color: #111; }
            h1 { text-align: center; margin: 0; font-size: 18px; }
            .subtitle { text-align: center; font-size: 12px; color: #555; margin-bottom: 16px; }
            .patient-info { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 6px; background: #f0f4f8; padding: 12px; border-radius: 6px; margin-bottom: 16px; font-size: 11px; }
            .patient-info div { display: flex; gap: 4px; }
            .patient-info b { color: #555; }
            .section-title { font-size: 13px; font-weight: bold; background: #1e3a5f; color: white; padding: 6px 10px; margin: 16px 0 8px; border-radius: 4px; }
            .visit-block { border: 1px solid #e5e7eb; border-radius: 6px; padding: 10px; margin-bottom: 10px; }
            .visit-header { font-weight: bold; font-size: 11px; background: #f9fafb; padding: 4px 8px; border-radius: 4px; margin-bottom: 6px; }
            p { margin: 3px 0; }
            ul { margin: 4px 0; padding-left: 20px; }
            .footer { margin-top: 20px; font-size: 10px; color: #999; text-align: center; }
        </style></head><body>
        <h1>Patient History Report</h1>
        <div class="subtitle">Generated: ${new Date().toLocaleString('en-PK')}</div>
        <div class="patient-info">
            <div><b>Name:</b> ${patient.full_name}</div>
            <div><b>MR#:</b> ${patient.mrn}</div>
            <div><b>CNIC:</b> ${patient.cnic || '-'}</div>
            <div><b>Age:</b> ${patient.age_years || '-'} yrs / ${patient.gender || '-'}</div>
            <div><b>Phone:</b> ${patient.phone || '-'}</div>
            <div><b>Blood Group:</b> ${patient.blood_group || '-'}</div>
        </div>
        ${(history.opd || []).length > 0 ? `<div class="section-title">OPD Visits (${history.opd.length})</div>${opdRows}` : ''}
        ${(history.ipd || []).length > 0 ? `<div class="section-title">IPD Admissions (${history.ipd.length})</div>${ipdRows}` : ''}
        ${(history.lab || []).length > 0 ? `<div class="section-title">Lab Results (${history.lab.length})</div>${labRows}` : ''}
        ${(history.radiology || []).length > 0 ? `<div class="section-title">Radiology (${history.radiology.length})</div>${radRows}` : ''}
        <div class="footer">HIMS — Hospital Information Management System</div>
        <script>window.onload=()=>{window.print();}</script>
        </body></html>`;
        const w = window.open('', '_blank');
        w.document.write(html);
        w.document.close();
    };

    const fmtDate = d => d ? new Date(d).toLocaleDateString('en-PK') : '-';
    const fmtDateTime = d => d ? new Date(d).toLocaleString('en-PK') : '-';

    return (
        <div style={{ maxWidth: 1000 }}>
                <div style={{ marginBottom: 20 }}>
                    <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: '#1e3a5f' }}>Patient History</h1>
                    <p style={{ margin: '4px 0 0', color: '#6b7280', fontSize: 13 }}>Complete OPD, IPD, Lab, Radiology history by MR#</p>
                </div>

                {/* Search Bar */}
                <div style={{ background: 'white', borderRadius: 10, padding: 16, marginBottom: 20, boxShadow: '0 1px 4px rgba(0,0,0,0.08)', display: 'flex', gap: 10 }}>
                    <input
                        value={mrn} onChange={e => setMrn(e.target.value)} onKeyDown={handleKey}
                        placeholder="Enter MR# (e.g. MR-00123) or Patient Name..."
                        style={{ flex: 1, padding: '10px 14px', border: '1px solid #d1d5db', borderRadius: 7, fontSize: 14, outline: 'none' }}
                    />
                    <button onClick={search} disabled={loading}
                        style={{ padding: '10px 24px', background: '#1e3a5f', color: 'white', border: 'none', borderRadius: 7, fontWeight: 600, cursor: 'pointer', fontSize: 14 }}>
                        {loading ? 'Searching...' : 'Search'}
                    </button>
                    {history && (
                        <button onClick={printHistory}
                            style={{ padding: '10px 20px', background: '#0369a1', color: 'white', border: 'none', borderRadius: 7, fontWeight: 600, cursor: 'pointer', fontSize: 14 }}>
                            Print History
                        </button>
                    )}
                </div>

                {/* Patient Card */}
                {patient && (
                    <div style={{ background: 'white', borderRadius: 10, padding: 16, marginBottom: 16, boxShadow: '0 1px 4px rgba(0,0,0,0.08)', borderLeft: '4px solid #1e3a5f' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
                            <div>
                                <div style={{ fontSize: 18, fontWeight: 700, color: '#1e3a5f' }}>{patient.full_name}</div>
                                <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>
                                    MR#: <b>{patient.mrn}</b> &nbsp;|&nbsp; {patient.age_years || '-'} yrs / {patient.gender || '-'} &nbsp;|&nbsp; {patient.phone || '-'}
                                </div>
                            </div>
                            <div style={{ fontSize: 12, color: '#374151', textAlign: 'right' }}>
                                <div>CNIC: {patient.cnic || '-'}</div>
                                <div>Blood: {patient.blood_group || '-'}</div>
                            </div>
                        </div>
                    </div>
                )}

                {/* History Sections */}
                {history && (
                    <>
                        {/* OPD */}
                        <Section title="OPD Visits" icon="🏥" count={history.opd?.length || 0} color="#0369a1">
                            {(history.opd || []).length === 0 ? <div style={{ color: '#9ca3af', fontSize: 13 }}>No OPD visits</div> :
                                history.opd.map((v, i) => (
                                    <div key={i} style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: 12, marginBottom: 10 }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                                            <div style={{ fontWeight: 700, fontSize: 13, color: '#0369a1' }}>
                                                {v.visit_date || fmtDate(v.created_at)} — {v.department || 'General'}
                                            </div>
                                            <div style={{ fontSize: 12, color: '#6b7280' }}>Dr. {v.doctor || 'N/A'}</div>
                                        </div>
                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4 }}>
                                            <Row label="Chief Complaint" value={v.chief_complaint} />
                                            <Row label="Diagnosis" value={v.diagnosis} />
                                            <Row label="Follow-up" value={v.follow_up_date} />
                                        </div>
                                        {v.doctor_notes && <div style={{ marginTop: 6, fontSize: 12, color: '#374151' }}><b>Doctor Notes:</b> {v.doctor_notes}</div>}
                                        {v.prescription && <div style={{ marginTop: 4, fontSize: 12, color: '#374151' }}><b>Prescription:</b> {typeof v.prescription === 'object' ? JSON.stringify(v.prescription) : v.prescription}</div>}
                                        {v.advice && <div style={{ marginTop: 4, fontSize: 12, color: '#374151' }}><b>Advice:</b> {v.advice}</div>}
                                    </div>
                                ))}
                        </Section>

                        {/* IPD */}
                        <Section title="IPD Admissions" icon="🛏️" count={history.ipd?.length || 0} color="#059669">
                            {(history.ipd || []).length === 0 ? <div style={{ color: '#9ca3af', fontSize: 13 }}>No IPD admissions</div> :
                                history.ipd.map((a, i) => (
                                    <div key={i} style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: 12, marginBottom: 10 }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                                            <div style={{ fontWeight: 700, fontSize: 13, color: '#059669' }}>
                                                {a.admission_number} — {a.ward_name || '-'} / Bed {a.bed_number || '-'}
                                            </div>
                                            <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 10, background: a.status === 'Admitted' ? '#dcfce7' : '#f1f5f9', color: a.status === 'Admitted' ? '#166534' : '#374151', fontWeight: 600 }}>
                                                {a.discharge_type || a.status}
                                            </span>
                                        </div>
                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4 }}>
                                            <Row label="Admitted" value={fmtDateTime(a.admission_time || a.admission_date)} />
                                            <Row label="Discharged" value={a.discharge_time ? fmtDateTime(a.discharge_time) : (a.status === 'Admitted' ? 'Still Admitted' : '-')} />
                                            <Row label="Consultant" value={a.consultant_name} />
                                            <Row label="Diagnosis" value={a.diagnosis} />
                                        </div>
                                        {a.discharge_summary && <div style={{ marginTop: 8, fontSize: 12, background: '#f0fdf4', padding: 8, borderRadius: 6 }}><b>Discharge Summary:</b> {a.discharge_summary}</div>}
                                        {a.cause_of_death && <div style={{ marginTop: 6, fontSize: 12, color: '#991b1b' }}><b>Cause of Death:</b> {a.cause_of_death}</div>}
                                        {(a.medications || []).length > 0 && (
                                            <div style={{ marginTop: 8 }}>
                                                <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 4 }}>Medications Given:</div>
                                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                                                    {a.medications.map((m, mi) => (
                                                        <span key={mi} style={{ fontSize: 11, background: '#eff6ff', color: '#1e40af', padding: '2px 8px', borderRadius: 10, border: '1px solid #bfdbfe' }}>
                                                            {m.name} {m.dose} {m.frequency}
                                                        </span>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                ))}
                        </Section>

                        {/* Lab */}
                        <Section title="Lab Results" icon="🔬" count={history.lab?.length || 0} color="#7c3aed">
                            {(history.lab || []).length === 0 ? <div style={{ color: '#9ca3af', fontSize: 13 }}>No lab orders</div> :
                                history.lab.map((o, i) => (
                                    <div key={i} style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: 12, marginBottom: 10 }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                                            <div style={{ fontWeight: 700, fontSize: 13, color: '#7c3aed' }}>{o.order_number} — {fmtDate(o.created_at)}</div>
                                            <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 10, background: o.status === 'Completed' ? '#dcfce7' : '#fef3c7', color: o.status === 'Completed' ? '#166534' : '#92400e', fontWeight: 600 }}>{o.status}</span>
                                        </div>
                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                                            {(o.tests || []).filter(t => t && t.test).map((t, ti) => (
                                                <div key={ti} style={{ fontSize: 12, background: t.flag === 'H' || t.flag === 'L' ? '#fef2f2' : '#f8fafc', padding: '4px 8px', borderRadius: 6, border: `1px solid ${t.flag === 'H' || t.flag === 'L' ? '#fecaca' : '#e5e7eb'}` }}>
                                                    <span style={{ color: '#6b7280' }}>{t.test}:</span> <b style={{ color: t.flag ? '#dc2626' : '#111' }}>{t.result || 'Pending'}</b> {t.unit}
                                                    {t.flag && <span style={{ color: '#dc2626', fontWeight: 700 }}> {t.flag}</span>}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                        </Section>

                        {/* Radiology */}
                        <Section title="Radiology / Imaging" icon="🩻" count={history.radiology?.length || 0} color="#0891b2">
                            {(history.radiology || []).length === 0 ? <div style={{ color: '#9ca3af', fontSize: 13 }}>No radiology orders</div> :
                                history.radiology.map((r, i) => (
                                    <div key={i} style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: 12, marginBottom: 10 }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                                            <div style={{ fontWeight: 700, fontSize: 13, color: '#0891b2' }}>{r.order_number} — {r.modality} {r.body_part && `(${r.body_part})`}</div>
                                            <div style={{ fontSize: 12, color: '#6b7280' }}>{fmtDate(r.created_at)}</div>
                                        </div>
                                        <Row label="Indication" value={r.clinical_indication} />
                                        <Row label="Referred by" value={r.referred_by} />
                                        {r.findings && <div style={{ marginTop: 6, fontSize: 12 }}><b>Findings:</b> {r.findings}</div>}
                                        {r.impression && <div style={{ marginTop: 4, fontSize: 12 }}><b>Impression:</b> {r.impression}</div>}
                                    </div>
                                ))}
                        </Section>

                        {/* A&E */}
                        {(history.ae || []).length > 0 && (
                            <Section title="Emergency (A&E) Visits" icon="🚨" count={history.ae?.length || 0} color="#dc2626">
                                {history.ae.map((e, i) => (
                                    <div key={i} style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: 12, marginBottom: 10 }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                                            <div style={{ fontWeight: 700, fontSize: 13, color: '#dc2626' }}>{e.ae_number} — {fmtDateTime(e.arrival_time)}</div>
                                            <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 10, background: '#fee2e2', color: '#991b1b', fontWeight: 600 }}>Triage: {e.triage_category || '-'}</span>
                                        </div>
                                        <Row label="Arrival Mode" value={e.arrival_mode} />
                                        <Row label="Disposition" value={e.disposition} />
                                        <Row label="Doctor" value={e.doctor} />
                                    </div>
                                ))}
                            </Section>
                        )}
                    </>
                )}

                {!history && !loading && (
                    <div style={{ textAlign: 'center', padding: 60, color: '#9ca3af' }}>
                        <div style={{ fontSize: 48, marginBottom: 12 }}>🔍</div>
                        <div style={{ fontSize: 14 }}>Enter MR# or patient name to search history</div>
                        <div style={{ fontSize: 12, marginTop: 4 }}>All OPD, IPD, Lab, and Radiology records will appear here</div>
                    </div>
                )}
        </div>
    );
}
